import { useState, useEffect } from 'react'
import { getCurrentUser } from '../services/firebaseService'
import AuthModal from './AuthModal'
import '../styles/components/paywall-popup.css'

function PaywallPopup({ isOpen, onClose, topicName, topicId }) {
  const [selectedPlan, setSelectedPlan] = useState('monthly')
  const [showAuth, setShowAuth] = useState(false)
  const [user, setUser] = useState(null)

  useEffect(() => {
    if (isOpen) {
      const currentUser = getCurrentUser()
      setUser(currentUser)
      if (!currentUser) {
        setShowAuth(true)
      }
    }
  }, [isOpen])

  const handleAuthSuccess = (user) => {
    setUser(user)
    setShowAuth(false)
  }

  if (!isOpen) return null

  const handleSelectPlan = (plan) => {
    if (!user) {
      setShowAuth(true)
      return
    }

    const baseUrl = window.location.origin
    const userId = user.uid
    
    // Determine base path (works from root or /dist/)
    const getBasePath = () => {
      const pathname = window.location.pathname
      if (pathname.includes('/dist/') || pathname === '/dist' || pathname.startsWith('/dist')) {
        return '/dist'
      }
      return ''
    }
    
    const basePath = getBasePath()
    // Use query params instead of path - works better with SPA routing
    // Include topicId so we can redirect back to the right topic after payment
    const successParams = new URLSearchParams({
      success: 'true',
      user_id: userId,
      plan: plan
    })
    if (topicId) {
      successParams.set('topic_id', topicId)
    }
    const successUrl = `${baseUrl}${basePath}/?${successParams.toString()}`
    
    // Store user_id, plan, and topicId in sessionStorage as backup (in case Stripe doesn't pass it)
    sessionStorage.setItem('parkort_payment_user_id', userId)
    sessionStorage.setItem('parkort_payment_plan', plan)
    if (topicId) {
      sessionStorage.setItem('parkort_payment_topic_id', topicId)
    }
    
    // Stripe Payment Links - try to append success_url parameter
    let paymentLink = plan === 'monthly' 
      ? (import.meta.env.VITE_STRIPE_PAYMENT_LINK_MONTHLY || 'https://buy.stripe.com/28EdR8dhk2fN4Evbaj7kc02')
      : (import.meta.env.VITE_STRIPE_PAYMENT_LINK_YEARLY || 'https://buy.stripe.com/dRm8wO9147A72wnemv7kc03')
    
    // Try to append success_url parameter (works if Stripe Payment Links support it)
    // If not, configure in Stripe Dashboard → Payment Links → Edit → After payment → Custom success page
    try {
      const url = new URL(paymentLink)
      url.searchParams.set('success_url', successUrl)
      paymentLink = url.toString()
    } catch (e) {
      // If URL parsing fails, use original payment link
      // Using original payment link, success URL configured in Stripe Dashboard
    }
    
    // Open payment link
    // After payment, Stripe will redirect to success URL (configured in Stripe Dashboard or via parameter)
    window.location.href = paymentLink
  }

  if (!isOpen) return null

  return (
    <>
      <AuthModal 
        isOpen={showAuth}
        onClose={() => {
          setShowAuth(false)
          if (!user) {
            onClose() // Close paywall if user cancels auth
          }
        }}
        onSuccess={handleAuthSuccess}
        mode="signin"
      />
      
      <div className="paywall-overlay" onClick={onClose}>
        <div className="paywall-popup" onClick={(e) => e.stopPropagation()}>
        <button 
          className="paywall-close" 
          onClick={onClose}
          aria-label="Luk popup"
        >
          ×
        </button>

        <div className="paywall-header">
          <h2>🔒 Premium Modul</h2>
          <p>Få adgang til <strong>{topicName}</strong> og alle andre Premium moduler</p>
          {!user && (
            <p style={{ 
              fontSize: '0.9em', 
              color: 'var(--text-secondary)', 
              marginTop: '8px',
              fontStyle: 'italic'
            }}>
              💡 Log ind for at få Premium på alle dine enheder
            </p>
          )}
        </div>

        <div className="paywall-plans">
          {/* Monthly Plan */}
          <div 
            className={`paywall-plan ${selectedPlan === 'monthly' ? 'selected' : ''}`}
            onClick={() => setSelectedPlan('monthly')}
            role="button"
            tabIndex={0}
            aria-label="Vælg månedligt abonnement"
            aria-pressed={selectedPlan === 'monthly'}
          >
            <div className="plan-header">
              <h3>Månedligt</h3>
              <div className="plan-price">
                <span className="price-amount">19 kr</span>
                <span className="price-period">/måned</span>
              </div>
            </div>
            <ul className="plan-features">
              <li>✅ Alle Premium moduler</li>
              <li>✅ Dybere analyse</li>
              <li>✅ Detaljeret feedback</li>
              <li>✅ Opgrader når som helst</li>
            </ul>
            <button 
              className="plan-select-btn"
              onClick={() => handleSelectPlan('monthly')}
            >
              Vælg månedligt →
            </button>
          </div>

          {/* Yearly Plan */}
          <div 
            className={`paywall-plan ${selectedPlan === 'yearly' ? 'selected' : 'featured'}`}
            onClick={() => setSelectedPlan('yearly')}
            role="button"
            tabIndex={0}
            aria-label="Vælg årligt abonnement"
            aria-pressed={selectedPlan === 'yearly'}
          >
            <div className="plan-badge">Anbefalet</div>
            <div className="plan-header">
              <h3>Årligt</h3>
              <div className="plan-price">
                <span className="price-amount">99 kr</span>
                <span className="price-period">/år</span>
              </div>
              <div className="plan-savings">Spar 57%</div>
            </div>
            <ul className="plan-features">
              <li>✅ Alle Premium moduler</li>
              <li>✅ Dybere analyse</li>
              <li>✅ Detaljeret feedback</li>
              <li>✅ Bedste værdi</li>
            </ul>
            <button 
              className="plan-select-btn plan-select-btn-primary"
              onClick={() => handleSelectPlan('yearly')}
            >
              Vælg årligt →
            </button>
          </div>
        </div>

        <div className="paywall-footer">
          <p style={{ fontSize: '0.85em', color: 'var(--text-secondary)', textAlign: 'center' }}>
            🔒 Sikker betaling via Stripe. Opsig når som helst.
          </p>
        </div>
      </div>
      </div>
    </>
  )
}

export default PaywallPopup
